import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, PenLine, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/sign/${token}`)
      .then(r => { setData(r.data); setName(r.data.signer || ""); })
      .catch(e => setErr(e?.response?.data?.detail || "Invalid link"));
  }, [token]);

  const sign = async () => {
    if (!name.trim()) return toast.error("Type your full name to sign");
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/sign/${token}`, { typed_name: name });
      toast.success("Signed — HCD Legal notified; your signed PDF is ready");
      const r = await axios.get(`${BACKEND_URL}/api/sign/${token}`);
      setData({ ...r.data, signed_ip: res.data.ip, signed_document_id: res.data.signed_document_id });
    } catch (e) { toast.error(e?.response?.data?.detail || "Signing failed"); }
    finally { setBusy(false); }
  };

  const downloadSigned = () => {
    window.location.href = `${BACKEND_URL}/api/sign/${token}/document`;
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="bg-white border p-8 rounded-md">{err}</div></div>;
  if (!data) return <div className="p-8 text-slate-500">Loading...</div>;

  const signed = data.status === "signed";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="sign-page">
      <div className="bg-white border border-slate-200 rounded-md w-full max-w-lg p-8">
        <div className="w-12 h-12 rounded-md bg-[#005289] flex items-center justify-center text-white font-bold mb-4">HCD</div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Sign contract #{data.contract_number}</h1>
        <div className="text-sm text-slate-600 mb-6">
          Vendor: <b>{data.vendor}</b><br />
          Signer: <b>{data.signer}</b> ({data.signer_role})
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-slate-700 mb-6">
          Your typed name below is captured as an electronic signature. On completion, a signed PDF certificate
          (with your name, timestamp, IP address, and audit trail) is generated automatically and filed against the contract.
        </div>
        {signed ? (
          <div className="space-y-3">
            <div className="p-4 bg-green-50 border border-green-200 rounded-md flex items-center gap-2" data-testid="sign-completed-panel">
              <CheckCircle2 className="w-5 h-5 text-green-700" />
              <div>
                <div className="font-bold text-green-900">Signed</div>
                <div className="text-xs text-green-800">{new Date(data.signed_at).toLocaleString()}</div>
              </div>
            </div>
            <button onClick={downloadSigned} className="w-full h-10 rounded-md text-white font-semibold flex items-center justify-center gap-2"
                    style={{ background: "#00AE42" }} data-testid="sign-download">
              <Download className="w-4 h-4" /> Download signed PDF certificate
            </button>
          </div>
        ) : (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">Type your full name to sign</label>
            <input value={name} onChange={e => setName(e.target.value)}
                   className="w-full h-11 px-3 text-lg border border-slate-200 rounded-md font-serif italic"
                   style={{ fontFamily: "Georgia, serif" }} data-testid="sign-name" />
            <button onClick={sign} disabled={busy}
                    className="mt-4 w-full h-10 rounded-md text-white font-semibold disabled:opacity-60"
                    style={{ background: "#005289" }} data-testid="sign-submit">
              <PenLine className="w-4 h-4 inline mr-2" />
              {busy ? "Signing..." : "Sign & generate PDF"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
